import { useState, useEffect } from 'react';

interface SectionItem {
  id: string;
  label: string;
  icon: string;
}

interface NavBarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  sections: SectionItem[];
  role: 'investigador' | 'ugpp' | null;
  onChangeRole: () => void;
}

export default function NavBar({ activeSection, onSectionChange, sections, role, onChangeRole }: NavBarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const roleLabel = role === 'investigador' ? 'Investigador' : role === 'ugpp' ? 'UGPP / Comité' : '';
  const roleColor =
    role === 'investigador'
      ? 'bg-white/20 text-white border-white/30'
      : 'bg-white/10 text-white border-white/20';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#003F7D] shadow-md'
          : 'bg-[#003F7D]'
      }`}
    >
      {/* Top accent bar */}
      <div className="w-full h-1 bg-gradient-to-r from-[#F5A623] via-[#F5A623]/70 to-[#003F7D]" />

      <div className="w-full px-6 py-2 flex items-center justify-between gap-4">
        {/* Logo + Title */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Logo con fondo blanco redondeado para mayor visibilidad */}
          <div className="w-20 h-20 flex items-center justify-center flex-shrink-0 bg-white rounded-xl p-1.5 shadow-md">
            <img
              src="https://static.readdy.ai/image/15adce3e38297225eba5a769409d1488/aa557116e21533bd8236cb99d49327c5.png"
              alt="Logo UPAL"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <p className="text-xs text-[#F5A623] font-bold tracking-widest uppercase leading-none mb-0.5">
              Universidad Privada Abierta Latinoamericana
            </p>
            <h1 className="text-base font-bold text-white leading-tight">Vicerrectorado de Investigación</h1>
            <p className="text-xs text-white/60 font-medium">Sistema UGPP</p>
          </div>
        </div>

        {/* Desktop navigation */}
        <div className="hidden xl:flex items-center gap-1 flex-1 justify-center">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => onSectionChange(s.id)}
              className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all cursor-pointer ${
                activeSection === s.id
                  ? 'bg-white/20 text-white border border-white/30'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={s.icon} />
              </span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Role badge + change role */}
        <div className="hidden xl:flex items-center gap-2 flex-shrink-0">
          {role && (
            <span
              className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full border ${roleColor}`}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={role === 'investigador' ? 'ri-user-line' : 'ri-building-line'} />
                </span>
                {roleLabel}
              </span>
            </span>
          )}
          <button
            onClick={onChangeRole}
            className="whitespace-nowrap flex items-center gap-1.5 text-xs text-white/70 hover:text-white px-3 py-1.5 rounded-md border border-white/20 hover:border-white/40 transition-all cursor-pointer"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-exchange-line" />
            </span>
            Cambiar perfil
          </button>
        </div>

        {/* Hamburger */}
        <button
          className="xl:hidden w-9 h-9 flex items-center justify-center text-white hover:bg-white/10 rounded-md cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="xl:hidden bg-[#002F6C] border-t border-white/10 px-4 pb-3">
          {role && (
            <div className="flex items-center justify-between py-2 mb-1 border-b border-white/10">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${roleColor}`}>
                {roleLabel}
              </span>
              <button
                onClick={() => { onChangeRole(); setMenuOpen(false); }}
                className="whitespace-nowrap text-xs text-white/60 flex items-center gap-1 cursor-pointer"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-exchange-line" />
                </span>
                Cambiar perfil
              </button>
            </div>
          )}
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => { onSectionChange(s.id); setMenuOpen(false); }}
              className={`whitespace-nowrap w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer mb-1 ${
                activeSection === s.id
                  ? 'bg-white/20 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${s.icon} text-base`} />
              </span>
              {s.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}
